import type { AuthSession } from '@jira/shared';
import axios, { type AxiosError, type AxiosInstance, type InternalAxiosRequestConfig } from 'axios';

const baseURL = import.meta.env.VITE_API_URL ?? '/api';

export const api: AxiosInstance = axios.create({
  baseURL,
  withCredentials: true,
  timeout: 20_000,
});

export function setAuthHeader(token: string | null): void {
  if (token) {
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  } else {
    delete api.defaults.headers.common['Authorization'];
  }
}

type RefreshHandler = (currentRefreshToken: string) => Promise<AuthSession>;
type LogoutHandler = () => void;
type GetRefreshToken = () => string | null;

interface AuthInterceptorOptions {
  getRefreshToken: GetRefreshToken;
  onRefreshed: (session: AuthSession) => void;
  onLogout: LogoutHandler;
}

let refreshPromise: Promise<AuthSession> | null = null;

export function attachAuthInterceptor(options: AuthInterceptorOptions): () => void {
  const doRefresh: RefreshHandler = async (currentRefreshToken) => {
    const { data } = await api.post<AuthSession>(
      '/auth/refresh',
      { refreshToken: currentRefreshToken },
      { headers: { _skipAuth: 'true' } },
    );
    return data;
  };

  const id = api.interceptors.response.use(
    (res) => res,
    async (error: AxiosError) => {
      const status = error.response?.status;
      const originalReq = error.config as
        | (InternalAxiosRequestConfig & { _retry?: boolean })
        | undefined;

      if (status !== 401 || !originalReq || originalReq._retry || originalReq.headers?._skipAuth) {
        return Promise.reject(error);
      }

      // 401s coming from upstream integrations (e.g. Jira credentials being
      // rejected) shouldn't trigger our access-token refresh + retry — that
      // just doubles the network calls and confuses the server log.
      const responseData = error.response?.data as { code?: string } | undefined;
      if (responseData?.code === 'JIRA_AUTH_FAILED') {
        return Promise.reject(error);
      }

      const rt = options.getRefreshToken();
      if (!rt) {
        options.onLogout();
        return Promise.reject(error);
      }

      if (!refreshPromise) {
        refreshPromise = doRefresh(rt)
          .catch((e) => {
            options.onLogout();
            throw e;
          })
          .finally(() => {
            // Clear after resolution so a subsequent 401 can trigger a fresh refresh.
            queueMicrotask(() => {
              refreshPromise = null;
            });
          });
      }

      try {
        const session = await refreshPromise;
        options.onRefreshed(session);
        originalReq._retry = true;
        originalReq.headers = originalReq.headers ?? {};
        originalReq.headers.Authorization = `Bearer ${session.accessToken}`;
        return api(originalReq);
      } catch (e) {
        return Promise.reject(e);
      }
    },
  );

  return () => api.interceptors.response.eject(id);
}

export function isApiError(
  e: unknown,
): e is AxiosError<{ code: string; message: string; details?: unknown }> {
  return axios.isAxiosError(e);
}

export function extractApiErrorMessage(e: unknown, fallback = 'Something went wrong'): string {
  if (isApiError(e)) {
    return e.response?.data?.message ?? fallback;
  }
  if (e instanceof Error) return e.message;
  return fallback;
}

export function extractApiErrorCode(e: unknown): string | null {
  if (isApiError(e)) return e.response?.data?.code ?? null;
  return null;
}
